import React from 'react'
import styled from 'styled-components'
import { useRevealProps } from '../../MwPage/components/Reveal'
import { Eyebrow, Hi, Hl, Lede, sans, Section, SectionHead, Stack, teal } from '../../MwPage/styled'
import { useFocus } from '../focus'
import { Card, Column, GradientPill, H2, Tag, Tags, Wrap } from '../styled'

const Arrow = styled.p`
  margin: 14px 0 8px;
  font: 700 15px/1.3 ${sans};
  color: ${teal};
`

// the prototype's .bigq: Martin's statement on a dark card
const BigQuote = styled.figure`
  margin: 0;
  padding: 26px 22px;
  border-radius: 20px;
  background: linear-gradient(135deg, #1b2a33, #14181c);
  border: 1.5px solid #2b5560;
  box-shadow: 0 18px 50px rgba(31, 181, 204, 0.18);

  blockquote {
    margin: 0 0 12px;
    font: 800 clamp(1.3rem, 2.4vw, 1.5rem) / 1.25 ${sans};
    letter-spacing: -0.025em;
    color: #ffffff;
  }

  figcaption {
    font-size: 14px;
    color: #8fd3e0;
  }

  figcaption b {
    color: #ffffff;
  }
`

const Quote = ({ advice }) => (
  <BigQuote {...useRevealProps()}>
    <blockquote>
      {advice.quoteStart}
      {advice.quoteEnd}
    </blockquote>
    <figcaption>
      <b>{advice.quoteName}</b> · {advice.quoteRole}
    </figcaption>
  </BigQuote>
)

// a page without the sources shows Martin's statement alone
const QuoteOnly = () => {
  const { advice } = useFocus()
  return (
    <Section $ice>
      <Wrap>
        <Column $max={820}>
          <Quote advice={advice} />
        </Column>
      </Wrap>
    </Section>
  )
}

const Full = () => {
  const { advice } = useFocus()

  return (
    <Section $ice>
      <Wrap>
        <Stack $gap="clamp(24px, 3vw, 32px)">
          <SectionHead>
            <Eyebrow {...useRevealProps()}>{advice.eyebrow}</Eyebrow>
            <H2 {...useRevealProps()}>
              {advice.headline} <Hi>{advice.headlineAccent}</Hi>
            </H2>
            <Lede {...useRevealProps()}>
              {advice.lede[0]}
              <Hl>{advice.ledeHl[0]}</Hl>
              {advice.ledeJoin}
              <Hl>{advice.ledeHl[1]}</Hl>.
            </Lede>
          </SectionHead>
          <Column $max={820} $gap={22}>
            <Card {...useRevealProps()}>
              <Tags role="list">
                {advice.sources.map((source) => (
                  <Tag key={source} role="listitem">
                    {source}
                  </Tag>
                ))}
              </Tags>
              <Arrow>{advice.arrow}</Arrow>
              <GradientPill style={{ textTransform: 'none', letterSpacing: 0, fontSize: 14.5 }}>
                {advice.outcome}
              </GradientPill>
            </Card>
            <Quote advice={advice} />
          </Column>
        </Stack>
      </Wrap>
    </Section>
  )
}

/** "Why free advice falls short": the sources, the question, and Martin's statement; the statement alone on a trial page. */
const AdviceSection = () => (useFocus().advice.sources ? <Full /> : <QuoteOnly />)

export default AdviceSection
